import { Wordmark } from "./Wordmark";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Flame, Menu, X } from "lucide-react";
import { interceptNav } from "../lib/navGuard";
import { useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";

export const Navbar = ({ dark = false }) => {
  const { user, logout } = useAuth();
  const pv = user?.admin_preview;
  const exitPreview = () => api.post("/admin/preview", { enabled: false }).then(() => window.location.assign("/admin?tab=preview")).catch(() => {});
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const links = [
    { label: "Home", to: "/" },
    { label: "Pricing", to: "/pricing" },
  ];
  const authedLinks = [
    { label: "Dashboard", to: "/dashboard" },
    { label: "Study", to: "/study" },
    { label: "Mock Oral Board", to: "/interview" },
    { label: "Resume Builder", to: "/resume" },
    { label: "Fitness Plan", to: "/fitness", badge: "Beta" },
  ];
  const navLinks = user ? (user.role === "admin" ? [...authedLinks, { label: "Admin", to: "/admin" }] : authedLinks) : links;

  // Active Oral Board (or other guarded page): ask before abandoning it via any navbar action.
  const guardedTo = (to) => (e) => {
    if (interceptNav(() => { setOpen(false); navigate(to); })) e.preventDefault();
    else setOpen(false);
  };
  const handleLogout = async () => {
    if (interceptNav(() => handleLogout())) return;
    await logout();
    navigate("/");
  };

  const textColor = dark ? "text-white" : "text-slate-700";

  return (<>
    {pv && <div className={`text-white text-xs px-4 py-1.5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 ${pv.tier === "pro" ? "bg-amber-600" : "bg-sky-700"}`} data-testid="preview-banner"><span className="font-bold uppercase tracking-wider text-[10px] bg-black/25 rounded px-1.5 py-0.5">Admin preview</span><span>Viewing as <b>{pv.tier === "pro" ? "Premium" : "Free"}</b> · <b>{pv.sample ? "sample user" : pv.fresh ? "brand-new user" : "existing user (your data)"}</b> — real data untouched.</span><Link to="/admin?tab=preview" className="underline font-semibold" data-testid="preview-banner-switch">Switch</Link><button type="button" onClick={exitPreview} className="underline font-semibold" data-testid="preview-banner-exit">Exit preview</button></div>}
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 dark:bg-slate-950/80 border-b border-slate-200/70 dark:border-slate-800/70">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" onClick={guardedTo("/")} className="flex items-center gap-2 group" data-testid="navbar-logo">
            <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center shadow-[0_0_16px_rgba(220,38,38,0.45)]">
              <Flame className="w-5 h-5 text-white" />
            </div>
            <Wordmark />
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={guardedTo(l.to)}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`text-sm font-semibold transition-colors hover:text-primary ${
                  location.pathname === l.to ? "text-primary" : "text-slate-900 dark:text-white"
                }`}
              >
                {l.label}{l.badge && <span className="ml-1 rounded-full bg-amber-100 text-amber-800 px-1.5 py-px text-[9px] font-bold uppercase tracking-wider align-middle" data-testid={`nav-badge-${l.to.slice(1)}`}>{l.badge}</span>}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <>
                <Link to="/account" onClick={guardedTo("/account")} className={`text-sm font-semibold transition-colors hover:text-primary ${location.pathname === "/account" ? "text-primary" : "text-slate-900 dark:text-white"}`} data-testid="navbar-account-link">Account</Link>
                <Link to="/account" onClick={guardedTo("/account")} title="Your profile" data-testid="navbar-avatar" className="w-9 h-9 rounded-full overflow-hidden border-2 border-slate-200 hover:border-primary transition-colors bg-slate-900 text-white flex items-center justify-center font-heading text-sm">
                  {user.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : (user.name || "?").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()}
                </Link>
                <Button variant="outline" size="sm" onClick={handleLogout} data-testid="navbar-logout-button">Log out</Button>
              </>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate("/auth")} data-testid="navbar-login-button">Log in</Button>
                <Button onClick={() => navigate("/auth?mode=register")} className="bg-primary hover:bg-primary/90" data-testid="navbar-signup-button">
                  Get Started
                </Button>
              </>
            )}
          </div>

          <button className="md:hidden text-slate-700 dark:text-white" onClick={() => setOpen(!open)} data-testid="navbar-mobile-toggle">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 px-4 py-4 space-y-2">
          {navLinks.map((l) => (
            <Link key={l.to} to={l.to} onClick={guardedTo(l.to)} className="block py-2 font-semibold text-slate-900 dark:text-white">
              {l.label}{l.badge && <span className="ml-1 rounded-full bg-amber-100 text-amber-800 px-1.5 py-px text-[9px] font-bold uppercase tracking-wider">{l.badge}</span>}
            </Link>
          ))}
          {user && <Link to="/account" onClick={guardedTo("/account")} className="block py-2 font-semibold text-slate-900 dark:text-white" data-testid="navbar-mobile-account">Account</Link>}
          {user ? (
            <Button variant="outline" className="w-full" onClick={handleLogout}>Log out</Button>
          ) : (
            <div className="flex flex-col gap-2 pt-2">
              <Button variant="outline" onClick={() => navigate("/auth")}>Log in</Button>
              <Button className="bg-primary" onClick={() => navigate("/auth?mode=register")}>Get Started</Button>
            </div>
          )}
        </div>
      )}
    </header>
  </>);
};
